import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../lib/api';

interface Language {
  name: string;
  percentage: number;
}

interface ScanResults {
  languages: Language[];
  frameworks: string[];
  testRunners: string[];
  cicd: { provider: string; files: string[] }[];
  coverageHints: string[];
  packageCount: number;
  keyDependencies: string[];
  repoSizeKB: number;
}

interface CodebaseScan {
  id: string;
  projectId: string;
  repoUrl: string;
  scanResults: ScanResults;
  scannedAt: string;
  scannedBy: string | null;
}

interface CodebaseResponse {
  connected: boolean;
  scan: CodebaseScan | null;
  githubTokenSet: boolean;
}

interface GenerateSummaryResponse {
  artefactId: string;
  overview: string;
  languageCount: number;
  frameworkCount: number;
  riskCount: number;
}

interface Props {
  projectId: string;
  latestSummaryArtefactId: string | null;
}

const cardCls = 'rounded-xl border shadow-sm transition-shadow duration-150';
const cardStyle: React.CSSProperties = {
  background: 'var(--surface)',
  borderColor: 'var(--border)',
  padding: 'var(--space-5)',
};

export default function CodebaseSidebarCard({ projectId, latestSummaryArtefactId }: Props) {
  const [data, setData] = useState<CodebaseResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [repoUrl, setRepoUrl] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatedArtefactId, setGeneratedArtefactId] = useState<string | null>(null);
  const [connectingGithub, setConnectingGithub] = useState(false);
  const cancelledRef = useRef(false);

  const handleConnectGithub = async () => {
    if (connectingGithub) return;
    setConnectingGithub(true);
    setError('');
    try {
      const res = await apiFetch<{ url: string }>('/byoi/github/connect');
      window.location.href = res.url;
    } catch (e) {
      if (!cancelledRef.current) {
        setError(e instanceof Error ? e.message : 'Failed to start GitHub connect');
        setConnectingGithub(false);
      }
    }
  };

  useEffect(() => {
    cancelledRef.current = false;
    if (!projectId) return;
    setLoading(true);
    setError('');
    apiFetch<CodebaseResponse>(`/projects/${projectId}/codebase`)
      .then(d => { if (!cancelledRef.current) setData(d); })
      .catch(e => { if (!cancelledRef.current) setError(e instanceof Error ? e.message : 'Failed to load codebase'); })
      .finally(() => { if (!cancelledRef.current) setLoading(false); });
    return () => { cancelledRef.current = true; };
  }, [projectId]);

  const handleConnect = async () => {
    const trimmed = repoUrl.trim();
    if (!trimmed || connecting) return;
    if (!trimmed.startsWith('https://')) {
      setError('Repository URL must start with https://');
      return;
    }
    setConnecting(true);
    setError('');
    try {
      const result = await apiFetch<{ connected: boolean; scan: CodebaseScan }>(
        `/projects/${projectId}/codebase/connect`,
        { method: 'POST', body: JSON.stringify({ repoUrl: trimmed }) },
      );
      if (cancelledRef.current) return;
      setData({ connected: true, scan: result.scan, githubTokenSet: true });
      setRepoUrl('');
    } catch (e) {
      if (cancelledRef.current) return;
      setError(e instanceof Error ? e.message : 'Connect failed');
    } finally {
      if (!cancelledRef.current) setConnecting(false);
    }
  };

  const handleRescan = async () => {
    if (scanning) return;
    setScanning(true);
    setError('');
    try {
      const result = await apiFetch<{ connected: boolean; scan: CodebaseScan }>(
        `/projects/${projectId}/codebase/scan`,
        { method: 'POST' },
      );
      if (cancelledRef.current) return;
      setData({ connected: true, scan: result.scan, githubTokenSet: true });
    } catch (e) {
      if (cancelledRef.current) return;
      setError(e instanceof Error ? e.message : 'Re-scan failed');
    } finally {
      if (!cancelledRef.current) setScanning(false);
    }
  };

  const handleGenerateSummary = async () => {
    if (generating) return;
    setGenerating(true);
    setError('');
    try {
      const result = await apiFetch<GenerateSummaryResponse>(
        `/projects/${projectId}/codebase/generate-summary`,
        { method: 'POST' },
      );
      if (cancelledRef.current) return;
      setGeneratedArtefactId(result.artefactId);
    } catch (e) {
      if (cancelledRef.current) return;
      setError(e instanceof Error ? e.message : 'Generate failed');
    } finally {
      if (!cancelledRef.current) setGenerating(false);
    }
  };

  const summaryArtefactId = generatedArtefactId ?? latestSummaryArtefactId;
  const scan = data?.scan?.scanResults;
  const primaryLanguage = scan?.languages?.[0];
  const primaryFramework = scan?.frameworks?.[0];
  const primaryTestRunner = scan?.testRunners?.[0];
  const hasCoverage = (scan?.coverageHints?.length ?? 0) > 0;

  return (
    <div className={cardCls} style={cardStyle}>
      <SectionHeader>Codebase</SectionHeader>

      {error && (
        <div data-testid="codebase-sidebar-error" style={{
          padding: '6px 10px', background: 'rgba(239,68,68,0.08)', color: 'var(--error)',
          borderRadius: 6, marginBottom: 10, fontSize: 12,
        }}>
          {error}
        </div>
      )}

      {loading && (
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>
          Loading…
        </p>
      )}

      {/* State 0: GitHub account not connected — launch OAuth inline so the
          user doesn't have to hop to the Settings page first. */}
      {!loading && data && !data.githubTokenSet && (
        <div data-testid="codebase-sidebar-no-token" style={{
          padding: 12,
          background: 'rgba(245,158,11,0.08)',
          border: '1px solid rgba(245,158,11,0.35)',
          borderRadius: 'var(--radius-sm)',
        }}>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: 'var(--text)', margin: 0, marginBottom: 4 }}>
            GitHub account not connected
          </p>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', margin: 0, marginBottom: 10 }}>
            Connect your GitHub so BluePrint can scan this codebase.
          </p>
          <button
            type="button"
            onClick={() => void handleConnectGithub()}
            disabled={connectingGithub}
            data-testid="codebase-sidebar-connect-github"
            style={{
              width: '100%', padding: '8px 12px', fontSize: 13, fontWeight: 600,
              background: '#1f2328', color: '#fff', border: 'none',
              borderRadius: 'var(--radius-sm)',
              cursor: connectingGithub ? 'not-allowed' : 'pointer',
              opacity: connectingGithub ? 0.5 : 1,
              marginBottom: 8,
            }}
          >
            {connectingGithub ? 'Redirecting…' : 'Connect GitHub'}
          </button>
          <Link
            to="/settings?section=byoi"
            data-testid="codebase-sidebar-pat-link"
            style={{ fontSize: 12, color: 'var(--text-secondary)', textDecoration: 'underline' }}
          >
            Use a personal access token instead (GHES)
          </Link>
        </div>
      )}

      {/* State 1: token set but no scan yet */}
      {!loading && data && data.githubTokenSet && !data.connected && (
        <div data-testid="codebase-sidebar-connect">
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 10 }}>
            Connect a GitHub repo to scan tech stack, test coverage, and CI/CD config.
          </p>
          <input
            type="text"
            aria-label="GitHub repository URL"
            placeholder="https://github.com/owner/repo"
            value={repoUrl}
            onChange={e => setRepoUrl(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') void handleConnect(); }}
            disabled={connecting}
            style={{
              width: '100%', padding: '8px 10px', fontSize: 13, boxSizing: 'border-box',
              border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
              background: 'var(--bg)', color: 'var(--text)',
              marginBottom: 8,
            }}
          />
          <button
            type="button"
            onClick={() => void handleConnect()}
            disabled={connecting || !repoUrl.trim()}
            style={{
              width: '100%', padding: '8px 12px', fontSize: 13, fontWeight: 600,
              background: 'var(--primary)', color: '#fff', border: 'none',
              borderRadius: 'var(--radius-sm)',
              cursor: connecting || !repoUrl.trim() ? 'not-allowed' : 'pointer',
              opacity: connecting || !repoUrl.trim() ? 0.5 : 1,
            }}
          >
            {connecting ? 'Scanning…' : 'Connect & Scan'}
          </button>
        </div>
      )}

      {/* State 2 & 3: scan exists (only when token is still configured) */}
      {!loading && data?.githubTokenSet && data?.connected && data.scan && (
        <div data-testid="codebase-sidebar-summary">
          {data.scan.repoUrl.startsWith('https://') ? (
            <a
              href={data.scan.repoUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: 13, fontWeight: 600, color: 'var(--primary)', textDecoration: 'none', wordBreak: 'break-all' }}
            >
              {data.scan.repoUrl.replace('https://github.com/', '')}
            </a>
          ) : (
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', wordBreak: 'break-all' }}>
              {data.scan.repoUrl}
            </span>
          )}

          <dl style={{ display: 'flex', flexDirection: 'column', gap: 4, margin: '10px 0' }}>
            {primaryLanguage && (
              <StatRow label="Language" value={`${primaryLanguage.name} (${primaryLanguage.percentage}%)`} />
            )}
            {primaryFramework && (
              <StatRow label="Framework" value={primaryFramework} />
            )}
            {primaryTestRunner && (
              <StatRow label="Test runner" value={primaryTestRunner} />
            )}
            <StatRow label="Coverage" value={hasCoverage ? 'Configured' : 'None detected'} />
          </dl>

          <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', margin: 0, marginBottom: 10 }}>
            Last scanned: {new Date(data.scan.scannedAt).toLocaleString()}
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button
              type="button"
              onClick={() => void handleRescan()}
              disabled={scanning}
              data-testid="codebase-sidebar-rescan"
              style={{
                padding: '6px 12px', fontSize: 12, fontWeight: 600,
                border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                background: 'transparent', color: 'var(--text)',
                cursor: scanning ? 'not-allowed' : 'pointer',
                opacity: scanning ? 0.5 : 1,
              }}
            >
              {scanning ? 'Scanning…' : 'Re-scan'}
            </button>

            {summaryArtefactId ? (
              <Link
                to={`/artefacts?tabs=${summaryArtefactId}&active=${summaryArtefactId}`}
                data-testid="codebase-sidebar-view-summary"
                style={{ fontSize: 13, color: 'var(--primary)', textDecoration: 'none', fontWeight: 500 }}
              >
                View Codebase Summary →
              </Link>
            ) : (
              <button
                type="button"
                onClick={() => void handleGenerateSummary()}
                disabled={generating}
                data-testid="codebase-sidebar-generate-summary"
                style={{
                  padding: 0, fontSize: 13, fontWeight: 500,
                  background: 'none', border: 'none', color: 'var(--primary)',
                  textAlign: 'left', cursor: generating ? 'not-allowed' : 'pointer',
                  opacity: generating ? 0.5 : 1,
                }}
              >
                {generating ? 'Generating…' : 'Generate Codebase Summary →'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <p
      className="uppercase tracking-wider"
      style={{
        fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)',
        marginBottom: 'var(--space-3)', letterSpacing: '0.05em',
        borderLeft: '3px solid var(--primary)', paddingLeft: 8,
      }}
    >
      {children}
    </p>
  );
}

function StatRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
      <dt style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>{label}</dt>
      <dd style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text)', margin: 0, fontWeight: 500, textAlign: 'right' }}>{value}</dd>
    </div>
  );
}
